import { AlertTriangle, Search } from "lucide-react";
import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Attributes, StepComponentProps } from "../step-config-file/types";

interface InsightItem {
  label: string | any;
  value: string | number | any;
  uom?: string;
}

const InsightTable = <T extends InsightItem>({
  data,
  title,
  value,
}: {
  data: T[];
  title: string;
  value: string;
}) => (
  <AccordionItem value={value} className="border-b-0">
    <AccordionTrigger className="text-base font-bold capitalize">
      {title}
    </AccordionTrigger>
    <AccordionContent className="flex flex-col gap-1">
      {data.map((item, index) => (
        <div
          key={index}
          className="mx-4 flex items-center justify-between"
        >
          <span className="text-xs text-muted-foreground">{item.label}</span>
          <span className="text-sm">
            {typeof item.value === "number"
              ? Number.isInteger(item.value)
                ? item.value
                : item.value.toFixed(2)
              : item.value}{" "}
            {item.uom ?? null}
          </span>
        </div>
      ))}
    </AccordionContent>
  </AccordionItem>
);

interface FeatureCardProps {
  address: string;
  featureDetails: InsightItem[];
  floodDetails: InsightItem[];
  headerOneTitle: string;
  headerTwoTitle: string;
  style: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  address,
  featureDetails,
  floodDetails,
  headerOneTitle,
  headerTwoTitle,
  style,
}) => {
  const floodAlertThreshold = floodDetails.find(
    (x) => x.label === `${style} Year`,
  )?.value;

  const shouldShowAlert = floodAlertThreshold > 0;

  return (
    <div className="absolute bottom-4 right-4 top-20 z-900 w-95 overflow-auto group-has-data-[collapsible=icon]/sidebar-wrapper:top-16">
      <Card className="flex h-full flex-col overflow-hidden bg-card/95">
        <CardContent className="flex flex-1 flex-col overflow-auto">
          <div className="mb-4 flex items-center">
            <div className="flex flex-1 items-center gap-2">
              <Search size={32} />
              <div>
                <h6 className="text-base font-semibold">Feature Inspector</h6>
                <p className="text-sm text-muted-foreground">{address}</p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <Accordion
              type="multiple"
              defaultValue={["featureDetails", "floodDetails"]}
              className="border-0"
            >
              <InsightTable
                data={featureDetails}
                title={headerOneTitle}
                value="featureDetails"
              />

              {shouldShowAlert && (
                <Alert className="my-2 border-warning">
                  <AlertTriangle className="size-4" />
                  <AlertTitle>At risk of {style} year flood</AlertTitle>
                </Alert>
              )}

              <InsightTable
                data={floodDetails}
                title={headerTwoTitle}
                value="floodDetails"
              />
            </Accordion>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

interface CardContentData {
  featureDetails: InsightItem[];
  floodDetails: InsightItem[];
  headerOne: string;
  headerTwo: string;
}

const contentForCard = (
  attributes: Attributes,
  type: "flood-building-height" | "flood-cadastre" | null,
): CardContentData => {
  if (!attributes) {
    return {
      featureDetails: [],
      floodDetails: [],
      headerOne: "",
      headerTwo: "",
    };
  }
  const properties = attributes.properties;

  if (type === "flood-building-height") {
    const featureDetails = [
      { label: "Ground Elevation", value: properties.ground_elevation, uom: "m" },
      { label: "First Floor Elevation", value: properties.first_floor_elevation, uom: "m" },
      { label: "Roof Height", value: properties.roof_height, uom: "m" },
      { label: "Estimated Levels", value: properties.estimated_levels },
      { label: "Swimming Pool Adjacent", value: properties.swimming_pool_adjacent },
      { label: "Solar Panels", value: properties.solar_panel },
      { label: "Planning Zone", value: properties.planning_zone },
      { label: "Building PID", value: properties.building_pid },
      { label: "Area", value: properties.area, uom: "m²" },
      { label: "Basement", value: properties.basement },
      { label: "Garage", value: properties.garage },
    ];
    const floodDetails: InsightItem[] = [2, 5, 10, 20, 50, 100, 500, 1500].map(
      (x) => {
        const value = properties[`flood_height_ari${x}`];
        let modifiedVal = !!value ? value : 0;
        modifiedVal = modifiedVal === -9999 ? 0 : modifiedVal;
        return { label: `${x} Year`, value: modifiedVal, uom: "m" };
      },
    );

    return {
      featureDetails,
      floodDetails,
      headerOne: "Primary Buildings Details",
      headerTwo: "Flood Height",
    };
  }

  if (type === "flood-cadastre") {
    const featureDetails: InsightItem[] = [
      { label: "Parcel ID", value: properties.parcel_id },
      { label: "Lot", value: properties.lot },
      { label: "Plan", value: properties.plan },
      { label: "Title Status", value: properties.title_status },
      { label: "Parcel Type", value: properties.parcel_type },
      { label: "Cadastre PID", value: properties.cadastre_pid },
      { label: "Area", value: properties.area, uom: "m²" },
    ];
    const floodDetails: InsightItem[] = [2, 5, 10, 20, 50, 100, 500, 1500].map(
      (x) => {
        const value = properties[`flood_height_ari${x}`];
        let modifiedVal = !!value ? value : 0;
        modifiedVal = modifiedVal === -9999 ? 0 : modifiedVal;
        return { label: `${x} Year`, value: modifiedVal, uom: "m" };
      },
    );
    return {
      featureDetails,
      floodDetails,
      headerOne: "Primary Cadastre Details",
      headerTwo: "Flood height",
    };
  }
  return {
    featureDetails: [],
    floodDetails: [],
    headerOne: "",
    headerTwo: "",
  };
};

export const FeatureCardComponent = (props: StepComponentProps) => {
  // @ts-ignore
  const { style } = props.formState;

  const cardContent = contentForCard(
    props.attributes,
    props.selectedLayer as "flood-building-height" | "flood-cadastre" | null,
  );
  const populatedAttributes = props.attributes
    ? Object.values(props.attributes.properties).length > 0
    : false;

  if (props.attributes && populatedAttributes) {
    return (
      <FeatureCard
        address={props.attributes.properties.address}
        featureDetails={cardContent.featureDetails}
        floodDetails={cardContent.floodDetails}
        headerOneTitle={cardContent.headerOne}
        headerTwoTitle={cardContent.headerTwo}
        style={style}
      />
    );
  }

  return null;
};
